import { NextRequest, NextResponse } from 'next/server';
import descopeSdk from '@descope/node-sdk';
import { DEFAULT_PUBLIC_ROUTES, DESCOPE_SESSION_HEADER } from './constants';
import { getGlobalSdk } from './sdk';
import { mergeSearchParams } from './utils';
import { LogLevel } from '../types';
import { logger, setLogger } from './logger';

type MiddlewareOptions = {
	// The Descope project ID to use for authentication
	// Defaults to process.env.NEXT_PUBLIC_DESCOPE_PROJECT_ID
	projectId?: string;

	// The base URL to use for authentication
	// Defaults to process.env.NEXT_PUBLIC_DESCOPE_BASE_URL
	baseUrl?: string;

	// The URL to redirect to if the user is not authenticated
	// Defaults to process.env.SIGN_IN_ROUTE or '/sign-in' if not provided
	// NOTE: In case it contains query parameters that exist in the original URL, they will override the original query parameters. e.g. if the original URL is /page?param1=1&param2=2 and the redirect URL is /sign-in?param1=3, the final redirect URL will be /sign-in?param1=3&param2=2
	redirectUrl?: string;

	// An array of public routes that do not require authentication
	// In addition to the default public routes:
	// - process.env.SIGN_IN_ROUTE or /sign-in if not provided
	// - process.env.SIGN_UP_ROUTE or /sign-up if not provided
	publicRoutes?: string[];

	// An array of private routes that require authentication
	// If privateRoutes is defined, routes not listed in this array will default to public routes
	privateRoutes?: string[];

	// The log level to use for the middleware
	// Defaults to 'info'
	logLevel?: LogLevel;

	// The name of the session cookie to look for the JWT
	// Defaults to 'DS'
	// Note: The middleware will also look for the JWT in the Authorization header
	sessionCookieName?: string;

	// The name of the refresh token cookie
	// Defaults to 'DSR'
	// Used to refresh the session when the JWT expires
	refreshTokenCookieName?: string;
};

const getSessionJwt = (
	req: NextRequest,
	options: MiddlewareOptions
): string | undefined => {
	let jwt = req.headers?.get('Authorization')?.split(' ')[1];
	if (jwt) {
		return jwt;
	}

	jwt = req.cookies?.get(
		options?.sessionCookieName || descopeSdk.SessionTokenCookieName
	)?.value;
	if (jwt) {
		return jwt;
	}
	return undefined;
};

const getRefreshJwt = (
	req: NextRequest,
	options: MiddlewareOptions
): string | undefined => {
	const refreshJwt = req.cookies?.get(
		options?.refreshTokenCookieName || 'DSR'
	)?.value;
	return refreshJwt;
};

const matchWildcardRoute = (route: string, path: string) => {
	let regexPattern = route.replace(/[.+?^${}()|[\]\\]/g, '\\$&');

	// Convert wildcard (*) to match path segments only
	regexPattern = regexPattern.replace(/\*/g, '[^/]*');
	const regex = new RegExp(`^${regexPattern}$`);

	return regex.test(path);
};

const isPublicRoute = (req: NextRequest, options: MiddlewareOptions) => {
	// Ensure publicRoutes and privateRoutes are arrays, defaulting to empty arrays if not defined
	const { publicRoutes = [], privateRoutes = [] } = options;
	const { pathname } = req.nextUrl;

	const isDefaultPublicRoute = Object.values(DEFAULT_PUBLIC_ROUTES).includes(
		pathname
	);

	if (publicRoutes.length > 0) {
		if (privateRoutes.length > 0) {
			logger.warn(
				'Both publicRoutes and privateRoutes are defined. Ignoring privateRoutes.'
			);
		}
		return (
			isDefaultPublicRoute ||
			publicRoutes.some((route) => matchWildcardRoute(route, pathname))
		);
	}

	if (privateRoutes.length > 0) {
		return (
			isDefaultPublicRoute ||
			!privateRoutes.some((route) => matchWildcardRoute(route, pathname))
		);
	}

	// If no routes are provided, all routes are private
	return isDefaultPublicRoute;
};

const removeSessionHeader = (headers: Headers): Headers => {
	const requestHeaders = new Headers(headers);
	requestHeaders.delete(DESCOPE_SESSION_HEADER);
	return requestHeaders;
};

// returns a Middleware that checks if the user is authenticated
// if the user is not authenticated, it redirects to the redirectUrl
const createAuthMiddleware =
	(options: MiddlewareOptions = {}) =>
	async (req: NextRequest) => {
		setLogger(options.logLevel);
		logger.debug('[Auth middleware] Starts');

		const sessionJwt = getSessionJwt(req, options);

		// check if the user is authenticated
		let validToken = false;
		try {
			await getGlobalSdk({
				projectId: options.projectId,
				baseUrl: options.baseUrl
			}).validateJwt(sessionJwt);
			validToken = true;
			logger.debug('[Auth middleware] Session JWT is valid');
		} catch (err) {
			logger.debug('[Auth middleware] Failed to validate session JWT', err);

			// Try to validate the refresh token instead
			const refreshJwt = getRefreshJwt(req, options);
			if (refreshJwt) {
				logger.debug('[Auth middleware] Attempting to validate refresh token');
				try {
					await getGlobalSdk({
						projectId: options.projectId,
						baseUrl: options.baseUrl
					}).validateJwt(refreshJwt);

					logger.debug('[Auth middleware] Refresh token is valid');
					validToken = true;
				} catch (refreshErr) {
					logger.debug(
						'[Auth middleware] Refresh token validation failed',
						refreshErr
					);
					// Refresh token validation failed, continue to redirect logic below
				}
			}

			// If we don't have a valid session or refresh token and this is a private route, redirect
			if (!validToken && !isPublicRoute(req, options)) {
				const redirectUrl = options.redirectUrl || DEFAULT_PUBLIC_ROUTES.signIn;
				const url = req.nextUrl.clone();
				// Create a URL object for redirectUrl. 'http://example.com' is just a placeholder.
				const parsedRedirectUrl = new URL(redirectUrl, 'http://example.com');
				url.pathname = parsedRedirectUrl.pathname;

				const searchParams = mergeSearchParams(
					url.search,
					parsedRedirectUrl.search
				);
				if (searchParams) {
					url.search = searchParams;
				}
				logger.debug(`[Auth middleware] Redirecting to ${redirectUrl}`);
				return NextResponse.redirect(url);
			}
		}

		logger.debug('[Auth middleware] finishes');

		const headers = removeSessionHeader(req.headers);
		return NextResponse.next({
			request: {
				headers
			}
		});
	};

export default createAuthMiddleware;
